import {
  ButtonHTMLAttributes,
  CSSProperties,
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";

import "./Button.scss";

type ButtonVariant = "PRIMARY" | "SECONDARY" | "OUTLINED" | "TRANSPARENT";

const buttonVariantToClassMap: Record<ButtonVariant, string> = {
  PRIMARY: "button--primary",
  SECONDARY: "button--secondary",
  OUTLINED: "button--outlined",
  TRANSPARENT: "button--transparent",
};

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant: ButtonVariant;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  suffixDivider?: boolean;
  emblem?: ReactNode;
  large?: boolean;
  fullWidth?: boolean;
  style?: CSSProperties;
}

/**
 * Example usage:
 * <Button
 *    variant="PRIMARY"
 *    large
 *    disabled
 *    leftIcon={<WalletOutlineSVG />}
 *    rightIcon={<AvatarSVG />}
 *    suffixDivider
 *  >
 *    My wallet
 *  </Button>
 */
const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      variant,
      leftIcon,
      rightIcon,
      suffixDivider,
      emblem,
      large,
      fullWidth,
      children,
      className,
      style,
      ...props
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(ref, () => internalRef.current as HTMLButtonElement);

    return (
      <button
        ref={internalRef}
        style={style}
        className={`button ${buttonVariantToClassMap[variant]} ${
          large ? "button--large" : ""
        } ${fullWidth ? "button--full-width" : ""} ${className || ""}`}
        {...props}
      >
        <div className="button__container">
          {leftIcon && <div className="button__left-icon">{leftIcon}</div>}
          <div className="button__children">{children}</div>
          {suffixDivider && <div className="button__suffix-divider"></div>}
          {rightIcon && <div className="button__right-icon">{rightIcon}</div>}
        </div>
        {emblem && <div className="button__emblem">{emblem}</div>}
      </button>
    );
  },
);

export { Button };
